import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArchiveRestore, CalendarClock, Clock, FileSpreadsheet, Loader2, MapPin, RefreshCw, RotateCcw, Search, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, KpiCard } from "./common";
import { PaginationBar, TableRowsSkeleton, useDebounced, usePagedList } from "./PaginationBar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { api, downloadBlob, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";
import { RESTORE_WINDOW_DAYS } from "./DeletePropertyDialog";

const ROLE_LABEL = { super_admin: "Super Admin", municipal_admin: "Municipal Admin", supervisor: "Supervisor" };
const VIA_STYLE = {
  manual: "bg-rose-50 text-rose-800 border-rose-200",
  admin_no_demand: "bg-amber-50 text-amber-800 border-amber-200",
  super_admin: "bg-violet-50 text-violet-800 border-violet-200",
};

const WindowBadge = ({ row, testId }) => row.expired ? (
  <Badge variant="outline" className="text-[10px] gap-1 bg-slate-100 text-slate-700 border-slate-200" data-testid={testId}>
    <Clock className="h-2.5 w-2.5" /> Window closed
  </Badge>
) : (
  <Badge variant="outline" className={`text-[10px] gap-1 ${row.days_left <= 5 ? "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" : "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20"}`} data-testid={testId}>
    <CalendarClock className="h-2.5 w-2.5" /> {row.days_left} day{row.days_left === 1 ? "" : "s"} left
  </Badge>
);

export const ViaBadge = ({ row, testId }) => (
  <Badge variant="outline" className={`text-[10px] font-normal whitespace-nowrap ${VIA_STYLE[row.deleted_via] || (String(row.deleted_via || "").includes("undo") ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-muted text-muted-foreground")}`} data-testid={testId}>
    {row.via_label || row.deleted_via || "—"}
  </Badge>
);

/**
 * Phase 192 — "Deleted properties" (Municipal Admin → Properties tab, and the Super Admin › Deleted Properties page).
 * A UNIFIED recycle bin: every record in properties_trash (manual delete, no-demand cleanup, batch undo, data cleanup)
 * with a "Deleted via" column, the reason and the 30-day restore window.
 *   path          list endpoint (tenant `/t/{code}/properties/deleted` or platform `/platform/deleted-properties`)
 *   extraParams   e.g. {code} for the platform endpoint
 *   codeOf(row)   tenant code to call restore / purge against
 *   exportPath    Phase 193: Excel export endpoint (same filters as the list: search / state / via, all pages)
 *   initialSearch Phase 193: pre-filled search (an alert's "Open bin" hands over the first PIN)
 * Restore (inside the window) and permanent delete (after it) are Super Admin only — the server decides per row.
 */
export const DeletedPropertiesTab = ({ path, exportPath, extraParams = {}, codeOf, reloadKey = 0, initialSearch = "", testId = "deleted-properties" }) => {
  const [q, setQ] = useState(initialSearch || "");
  const search = useDebounced(q);
  const [state, setState] = useState("all");
  const [via, setVia] = useState("all");
  const [confirm, setConfirm] = useState(null);    // {kind: "restore" | "purge", row}
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { setQ(initialSearch || ""); }, [initialSearch]);
  const params = useMemo(() => ({ ...extraParams, search: search || undefined, state: state === "all" ? undefined : state, via: via === "all" ? undefined : via }),
    [JSON.stringify(extraParams), search, state, via]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = usePagedList(path, { params, initialLimit: 25, reloadKey });
  const counts = list.data?.counts || {};
  const access = list.data?.access || { is_super: false };
  const viaOptions = list.data?.via_options || [];
  const filtered = !!q || state !== "all" || via !== "all";

  // Phase 193: Excel of exactly what the filters show (every page)
  const exportExcel = async () => {
    if (!exportPath || exporting) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, v); });
      const code = extraParams.code || (codeOf ? codeOf({}) : "");
      await downloadBlob(`${exportPath}${qs.toString() ? `?${qs}` : ""}`, `${code || "deleted"}_deleted_properties.xlsx`);
      toast.success("Deleted properties exported", { description: filtered ? "Only the rows matching the current filters were exported (all pages)." : "The whole recycle bin was exported." });
    } catch (e) {
      toast.error("Could not export", { description: errMsg(e) });
    } finally {
      setExporting(false);
    }
  };

  const run = async () => {
    if (!confirm) return;
    const { kind, row } = confirm;
    const code = codeOf(row);
    setBusy(true);
    try {
      if (kind === "restore") {
        const r = await api.post(`/t/${code}/properties/deleted/${row.id}/restore`);
        toast.success(`Property ${r.data.pin} restored`, { description: `It is back in the register with ${r.data.demand_count} demand${r.data.demand_count === 1 ? "" : "s"}, its assignments and custom values.` });
      } else {
        const r = await api.delete(`/t/${code}/properties/deleted/${row.id}/permanent`);
        toast.success(`Property ${r.data.pin} permanently deleted`, { description: "An archive copy is kept for the audit trail only." });
      }
      pokeLiveCollections();
      setConfirm(null);
      list.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testId}-tab`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid={`${testId}-summary`}>
        <KpiCard label="Deleted properties" value={list.data ? (counts.total ?? 0) : "—"} icon={Trash2} testId={`${testId}-kpi-total`} sub="everything in the recycle bin" />
        <KpiCard label="Restorable" value={list.data ? (counts.restorable ?? 0) : "—"} icon={ArchiveRestore} testId={`${testId}-kpi-restorable`} sub={`inside the ${RESTORE_WINDOW_DAYS}-day window (Super Admin)`} />
        <KpiCard label="Window closed" value={list.data ? (counts.expired ?? 0) : "—"} icon={Clock} testId={`${testId}-kpi-expired`} sub="Super Admin may delete permanently" />
        <KpiCard label="Manual deletes" value={list.data ? (counts.manual ?? 0) : "—"} icon={ShieldAlert} testId={`${testId}-kpi-manual`} sub="deleted from the Properties page" />
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-3 sm:p-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Search PIN, owner, address, mobile, reason, deleted by…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search-input`} />
          </div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-44 h-9" data-testid={`${testId}-state-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All deleted</SelectItem>
              <SelectItem value="restorable">Restorable</SelectItem>
              <SelectItem value="expired">Window closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={via} onValueChange={setVia}>
            <SelectTrigger className="w-56 h-9" data-testid={`${testId}-via-filter`}><SelectValue placeholder="Deleted via" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Deleted via: all</SelectItem>
              {viaOptions.map((o) => <SelectItem key={o.key} value={o.key} data-testid={`${testId}-via-option-${o.key}`}>{o.label} ({o.count})</SelectItem>)}
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" className="h-9" onClick={() => { setQ(""); setState("all"); setVia("all"); }} data-testid={`${testId}-reset-filters`}>
              <X className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {exportPath && (
              <Button variant="outline" size="sm" className="h-9" onClick={exportExcel} disabled={exporting || !list.data || list.total === 0} data-testid={`${testId}-export-button`}>
                {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />}
                {exporting ? "Preparing…" : `Export Excel${list.data && filtered ? ` (${list.total.toLocaleString("en-IN")})` : ""}`}
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-9" onClick={() => list.reload()} disabled={list.loading} data-testid={`${testId}-refresh`}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid={`${testId}-error`}>
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">{errMsg(list.error)}</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid={`${testId}-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
            </div>
          ) : !list.data ? <TableRowsSkeleton rows={6} cols={8} testId={`${testId}-skeleton`} /> : list.items.length === 0 ? (
            <EmptyState title={filtered ? "No deleted properties match" : "Recycle bin is empty"}
              subtitle={filtered ? "Try another search or reset the filters." : `Properties deleted from the register appear here. The Super Admin can restore them for ${RESTORE_WINDOW_DAYS} days.`} />
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className={`overflow-x-auto ${list.loading ? "opacity-60 transition-opacity" : ""}`}>
                <Table data-testid={`${testId}-table`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead className="hidden md:table-cell">Area</TableHead>
                      <TableHead className="text-right hidden lg:table-cell">Demands</TableHead>
                      <TableHead>Deleted</TableHead>
                      <TableHead>Deleted via</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Restore window</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((r) => (
                      <TableRow key={r.id} className={r.expired ? "bg-muted/30" : ""} data-testid={`${testId}-row-${r.id}`} data-state-window={r.expired ? "expired" : "restorable"}>
                        <TableCell>
                          <p className="font-mono text-sm" data-testid={`${testId}-pin-${r.id}`}>{r.pin || "—"}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{r.owner_name || "—"}{r.house_no ? ` · ${r.house_no}` : ""}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{[r.zone_label, r.ward_label, r.mohalla_label].filter(Boolean).join(" • ") || "—"}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums hidden lg:table-cell">{r.demand_count ?? 0}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap" data-testid={`${testId}-deleted-${r.id}`}>
                          <p className="tabular-nums">{fmtDateTime(r.deleted_at)}</p>
                          <p className="text-muted-foreground truncate max-w-[160px]">{r.deleted_by_name || "—"}{ROLE_LABEL[r.deleted_by_role] ? ` · ${ROLE_LABEL[r.deleted_by_role]}` : ""}</p>
                        </TableCell>
                        <TableCell><ViaBadge row={r} testId={`${testId}-via-${r.id}`} /></TableCell>
                        <TableCell className="text-xs max-w-[240px]">
                          <Tooltip>
                            <TooltipTrigger asChild><p className="truncate" data-testid={`${testId}-reason-${r.id}`}>{r.delete_reason || <span className="text-muted-foreground">—</span>}</p></TooltipTrigger>
                            {r.delete_reason && <TooltipContent side="top" className="max-w-[320px] text-xs">{r.delete_reason}</TooltipContent>}
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          <WindowBadge row={r} testId={`${testId}-window-${r.id}`} />
                          <p className="text-muted-foreground mt-0.5">until {fmtDate(r.restore_until)}</p>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {r.can_restore && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirm({ kind: "restore", row: r })} data-testid={`${testId}-restore-${r.id}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                          )}
                          {r.can_purge && (
                            <Button size="sm" variant="destructive" className="h-8 ml-1.5" onClick={() => setConfirm({ kind: "purge", row: r })} data-testid={`${testId}-purge-${r.id}`}>
                              <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Delete permanently
                            </Button>
                          )}
                          {!r.can_restore && !r.can_purge && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground" data-testid={`${testId}-noaction-${r.id}`}>Super Admin only</span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="text-xs max-w-[260px]">
                                {r.expired ? `The ${RESTORE_WINDOW_DAYS}-day restore window has closed — only the Super Admin can permanently delete this property.`
                                  : `Only the Super Admin can restore a deleted property (within ${RESTORE_WINDOW_DAYS} days of deletion).`}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId={`${testId}-pagination`} />
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {!access.is_super && list.data && (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testId}-admin-note`}>
          Deleted properties are hidden from every view. Restoring (inside the {RESTORE_WINDOW_DAYS}-day window) and permanent deletion (after it) are done by the Super Admin from Super Admin → Deleted Properties.
        </p>
      )}

      <Dialog open={!!confirm} onOpenChange={(v) => !busy && !v && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              {confirm?.kind === "purge" ? <ShieldAlert className="h-5 w-5 text-destructive" /> : <RotateCcw className="h-5 w-5 text-primary" />}
              {confirm?.kind === "purge" ? "Permanently delete" : "Restore"} property {confirm?.row?.pin}?
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === "purge"
                ? `${confirm?.row?.owner_name || "Owner unknown"} — deleted ${fmtDate(confirm?.row?.deleted_at)} via ${confirm?.row?.via_label || "—"}. This cannot be undone: the property and its ${confirm?.row?.demand_count ?? 0} demand(s) leave the municipality's data for good (an archive copy stays for the audit trail).`
                : `${confirm?.row?.owner_name || "Owner unknown"} — deleted ${fmtDate(confirm?.row?.deleted_at)} via ${confirm?.row?.via_label || "—"}. The property comes back exactly as before with its ${confirm?.row?.demand_count ?? 0} demand(s), assignments and custom values.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} data-testid={`${testId}-confirm-cancel`}>Cancel</Button>
            <Button variant={confirm?.kind === "purge" ? "destructive" : "default"} onClick={run} disabled={busy} data-testid={`${testId}-confirm-ok`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : confirm?.kind === "purge" ? <ShieldAlert className="h-4 w-4 mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
              {busy ? "Working…" : confirm?.kind === "purge" ? "Delete permanently" : "Restore property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeletedPropertiesTab;
